import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, MapPin, Globe2, Scale, Database, Plug, Users } from "lucide-react";
import { PageHero, SectionWrapper, SectionHeader } from "../components/Section";

const CORE_MARKETS = [
  {
    id: "kosovo",
    code: "XK",
    name: "Kosovo",
    headline: "A practical market for phased institutional digitization.",
    body: [
      "Kosovo is a strategic market for Allinstra’s institutional systems approach. The country presents a clear opportunity for municipalities, public-service organizations, organized industrial zones, universities, hospitals, chambers, unions, and structured institutions that want to improve operational visibility, document control, workflow discipline, and phased digital management capacity.",
      "Allinstra’s Kosovo focus is built around practical localization, institutional modernization, and realistic implementation planning. The approach begins with what can create immediate institutional value: internal process structure, document and records management, service coordination, finance and revenue workflows, field operations, and industrial zone management.",
      "Future integration pathways can then be evaluated where public infrastructure, third-party access, legal framework, and technical conditions allow.",
    ],
    focus: [
      "Municipality and public-service systems",
      "Organized industrial zone and utility management",
      "Electronic document and records management",
      "Universities, hospitals, chambers, unions, and structured institutions",
      "Finance, revenue, procurement, and collection processes",
      "Field, infrastructure, and service coordination",
      "Phased localization and implementation",
      "Future integration readiness where infrastructure allows",
    ],
  },
  {
    id: "turkiye",
    code: "TR",
    name: "Türkiye",
    headline: "A reference market for institutional software maturity.",
    body: [
      "Türkiye represents an important reference and experience market for Allinstra’s institutional systems approach. Its public-service environments, municipality operations, organized industrial zones, utility-based structures, electronic document practices, and e-transformation experience provide a strong background for understanding complex institutional software needs.",
      "Allinstra’s Türkiye positioning is built around institutional software maturity, implementation experience, sector-specific operational knowledge, public-service digitization, industrial zone operations, and digital transformation familiarity.",
      "This market also provides valuable context for adapting institutional systems into other regions. Türkiye’s experience helps support Allinstra’s broader market adaptation logic while recognizing that each country requires its own legal, technical, and institutional review before implementation.",
    ],
    focus: [
      "Municipal and public-service systems",
      "Organized industrial zone systems",
      "Utility subscriber management",
      "Electronic document and records structures",
      "E-transformation-related workflows",
      "Finance, revenue, procurement, and asset processes",
      "Field, infrastructure, and spatial operations",
      "Institutional system experience and operational maturity",
      "Solution ecosystem and implementation knowledge",
    ],
  },
  {
    id: "uk",
    code: "GB",
    name: "United Kingdom",
    headline: "A selective opportunity for structured institutions.",
    body: [
      "The United Kingdom represents a selective market opportunity for Allinstra’s institutional systems approach, particularly in environments where structured organizations need stronger workflow visibility, document and records control, operational coordination, reporting discipline, and modular management systems.",
      "The UK opportunity is not framed around replacing standard municipal software environments. It is better aligned with structured institutions, document-heavy operations, universities, business parks, estate-style management needs, professional or semi-public operating environments, hospitals, regulated service organizations, and selected sector-specific institutions where modular systems can create operational value.",
      "This makes the United Kingdom relevant for use cases involving document control, internal workflows, structured service coordination, asset and process visibility, management reporting, portal access, and selected institutional operations that require clearer digital structure without assuming a public-sector integration model.",
    ],
    focus: [
      "Structured institution systems",
      "Universities, hospitals, business parks, and estate-style environments",
      "Document and records management",
      "Workflow visibility and operational control",
      "Selected sector-specific institutional systems",
      "Finance, procurement, and reporting workflows",
      "Portal and access structures where relevant",
      "Market-specific adaptation and feasibility review",
    ],
  },
];

const ADAPTATION_CARDS = [
  {
    icon: Scale,
    title: "Legal and regulatory context",
    items: [
      "Local legislation",
      "Procurement and contracting conditions",
      "Electronic signature and digital trust services",
    ],
  },
  {
    icon: Database,
    title: "Public infrastructure and data access",
    items: [
      "Public infrastructure availability",
      "E-government maturity",
      "Identity and address systems",
      "Land registry, cadastre, and GIS availability",
    ],
  },
  {
    icon: Plug,
    title: "Technical and integration readiness",
    items: [
      "Payment infrastructure",
      "Third-party access and API readiness",
      "Data quality and system documentation",
    ],
  },
  {
    icon: Users,
    title: "Institutional adoption",
    items: [
      "Language and terminology",
      "Institutional priorities",
      "Local implementation capacity",
    ],
  },
];

const Markets = () => (
  <main data-testid="page-markets">
    <PageHero
      overline="Markets"
      title="Structured for selected markets and regional growth."
      lead="Allinstra is designed for institutions that need stronger operational management, document control, workflow visibility, and integrated digital structures."
    >
      <p className="max-w-3xl text-[15px] text-ink-600 leading-relaxed">
        The initial market focus includes Kosovo, Türkiye, and the United Kingdom. The wider
        expansion logic covers selected opportunities across the European Union, the Balkans,
        Turkic States, and other markets where institutional digital management systems can
        create value.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/contact" data-testid="markets-hero-market" className="btn-primary">
          Discuss Your Market <ArrowUpRight size={16} />
        </Link>
        <Link
          to="/product-portfolio"
          data-testid="markets-hero-portfolio"
          className="btn-secondary"
        >
          View Product Portfolio <ArrowRight size={14} />
        </Link>
      </div>
    </PageHero>

    {CORE_MARKETS.map((m, idx) => (
      <section
        key={m.id}
        id={`market-${m.id}`}
        data-testid={`core-market-${m.id}`}
        className={`scroll-mt-24 border-t border-ink-200 ${
          idx % 2 === 0 ? "bg-white" : "bg-ink-50"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-700">
                Core Market · {m.code}
              </div>
              <div className="mt-4 w-12 h-12 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <MapPin size={20} strokeWidth={1.5} />
              </div>
              <h2 className="heading-display mt-5 text-3xl sm:text-4xl font-semibold">
                {m.name}
              </h2>
              <p className="mt-4 text-[15.5px] text-ink-700 leading-relaxed font-medium">
                {m.headline}
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="space-y-4 text-[16px] leading-relaxed text-ink-700">
                {m.body.map((p, i) => (
                  <p key={i} className={i === m.body.length - 1 ? "text-ink-900" : ""}>
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-8">
                <div className="tag-overline">Focus areas</div>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {m.focus.map((f, i) => (
                    <li
                      key={i}
                      data-testid={`core-market-${m.id}-focus-${i + 1}`}
                      className="flex items-start gap-3 text-[14px] text-ink-700"
                    >
                      <span
                        className="mt-1.5 w-1.5 h-1.5 bg-cyan-500 shrink-0"
                        aria-hidden="true"
                      />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    ))}

    <SectionWrapper id="markets-expansion" alt>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionHeader
            overline="Regional and International Expansion"
            title="Beyond core markets."
          />
          <div className="mt-6 inline-flex items-center gap-2 text-cyan-700">
            <Globe2 size={14} />
            <span className="font-mono text-[11px] uppercase tracking-widest">
              EU · Balkans · Turkic States · Selected international
            </span>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-5 text-[16px] leading-relaxed text-ink-700">
          <p>
            Allinstra may also support selected opportunities across the European Union, the
            Balkans, Turkic States, and other international markets where municipalities,
            industrial zones, public-service organizations, universities, hospitals, chambers,
            unions, or structured institutions need stronger digital management systems.
          </p>
          <p className="text-ink-900">
            Expansion is opportunity-led and implementation-focused. Each new market requires
            review of local legislation, public infrastructure, language and terminology,
            e-government maturity, payment and identity systems, procurement practices,
            institutional demand, and delivery capacity.
          </p>
        </div>
      </div>
    </SectionWrapper>

    <SectionWrapper id="markets-adaptation">
      <SectionHeader
        overline="Market Adaptation Principles"
        title="One portfolio, adapted to local realities."
      />
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {ADAPTATION_CARDS.map((c, i) => (
          <article
            key={c.title}
            data-testid={`adaptation-card-${i + 1}`}
            className="bg-white border border-ink-200 p-7 sm:p-8 card-hover flex flex-col"
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 border border-ink-200 inline-flex items-center justify-center text-cyan-600">
                <c.icon size={18} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                {String(i + 1).padStart(2, "0")} / 04
              </div>
            </div>
            <h3 className="heading-display mt-5 text-lg sm:text-xl font-semibold leading-snug">
              {c.title}
            </h3>
            <ul className="mt-4 space-y-2.5">
              {c.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[14.5px] text-ink-700">
                  <span
                    className="mt-1.5 w-1.5 h-1.5 bg-cyan-500 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </SectionWrapper>

    <SectionWrapper id="markets-final-cta" alt>
      <div className="border border-ink-200 p-8 sm:p-12 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <div className="tag-overline">Final step</div>
            <h2 className="heading-display mt-3 text-2xl sm:text-3xl font-semibold">
              Discuss your market with Allinstra.
            </h2>
            <p className="mt-3 text-[15px] text-ink-600 leading-relaxed max-w-2xl">
              If your institution, region, or market needs a clearer digital management
              structure, Allinstra can help evaluate the right scope, adaptation path, and
              implementation sequence.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap pt-2 lg:pt-0">
            <Link to="/contact" data-testid="markets-final-market" className="btn-primary">
              Discuss Your Market <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/product-portfolio"
              data-testid="markets-final-portfolio"
              className="btn-secondary"
            >
              View Product Portfolio
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  </main>
);

export default Markets;
